import React, { Fragment, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { isBlank } from "../../../util/ext";
import {
  addIndex,
  equals,
  identity,
  isEmpty,
  map,
  sortBy,
  toUpper,
} from "ramda";
import { useSelector } from "react-redux";
import { hasAdminRole } from "../../../auth/authUtils";
import { useContentUpdate } from "../../content/contentServices";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import clsx from "clsx";
import { useSnackAlert } from "../../../common/use-snackAlert";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import MediaDialog from "../../common/MediaDialog";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  actions: {
    padding: theme.spacing(5),
  },

  badge: {
    background: "rgba(125, 218, 252, 0.5)",
    borderRadius: 4,
    padding: "8px 12px",
    gap: 10,
    width: "auto",
    height: 32,
  },

  button: {
    width: "auto",
    textTransform: "capitalize",
  },

  mediaType: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "16px",
    color: theme.palette.primary.main,
  },

  otherReason: {
    lineHeight: "2.5rem",
    maxHeight: 150,
    overflowY: "auto !important",
  },

  rejected: {
    backgroundColor: theme.palette.rejected,
    "&:hover": {
      backgroundColor: "rgba(176, 34, 12, 0.7)",
    },
  },

  title: {
    color: theme.palette.primary.darker,
    fontSize: "2.4rem",
    lineHeight: "3.2rem",
  },
}));

const reasons = {
  admin: [
    "Author Request",
    "Duplicate",
    "File / metadata mismatch",
    "Inappropriate / harmful subject matter",
    "Not a book",
    "Only submitted a cover",
    "Proof copy / Advanced Reader Copy",
    "Formatting issues / Missing Pages / Pages out of order",
    "Poorly scanned / illegible content",
    "Lack of necessary information",
    "Watermarked",
    "Other (provide reason)",
  ],
  creatorUsers: [
    "Formatting issues / Missing Pages / Pages out of order",
    "File / metadata mismatch",
    "Inappropriate / harmful subject matter",
    "Poorly scanned / illegible content",
    "Lack of necessary information",
    "Duplicate",
    "Other (provide reason)",
  ],
};

const getReasons = user => {
  return hasAdminRole(user) ? reasons.admin : reasons.creatorUsers;
};

const mapIndexed = addIndex(map);

const RejectedDialog = props => {
  const { open, content, handleClose, handleReject, parents } = props;
  const classes = useStyles();
  const { SnackAlert, showAlert } = useSnackAlert();

  //-- Remove content from all parents prior to rejecting
  const [hasParents, setHasParents] = useState(!isEmpty(parents));

  const auth = useSelector(state => state.auth);
  const [rejectedReasons, setRejectedReasons] = useState([]);
  useEffect(() => {
    const reasons = getReasons(auth.user);
    setRejectedReasons(reasons);
  }, [auth]);

  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [error, setError] = useState(false);
  const handleChange = event => {
    const reason = event.target.value;
    setReason(reason);
    setError(false);
  };

  const resetReason = () => {
    setReason("");
    setOtherReason("");
  };

  const handleCloseClick = () => {
    handleClose();
    resetReason();
    setError(false);
  };

  const updateContent = useContentUpdate();
  const submit = async () => {
    const error =
      reason === "Other (provide reason)"
        ? isBlank(otherReason)
        : isBlank(reason);
    setError(error);
    if (!error) {
      try {
        const rejectionNote =
          reason === "Other (provide reason)" ? otherReason : reason;

        await updateContent.mutateAsync({
          resourceIds: { id: content.id },
          data: {
            stipProperties: [{ rejectionNote }],
            status: "REJECTED",
          },
        });

        handleCloseClick();
        handleReject();
      } catch (err) {
        console.log(err);
      }
    }
  };

  const removeContentFromCollection = useRemoveContentFromCollection();
  const deleteModuleItems = useDeleteModuleItems();
  const removeParents = async () => {
    try {
      await Promise.all(
        map(async parent => {
          try {
            if (equals(parent.mediaType, "module")) {
              await deleteModuleItems.mutateAsync({
                id: parent.id,
                ids: [content.id],
              });
            }

            if (equals(parent.mediaType, "collection")) {
              await removeContentFromCollection.mutateAsync({
                collectionId: parent.id,
                contentIds: [content.id],
              });
            }
          } catch (err) {
            return err.message;
          }
        }, parents),
      );

      // Add stuff that works here
      setHasParents(false);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };

  useEffect(() => {
    setHasParents(!isEmpty(parents));
  }, [parents]);

  return (
    <Fragment>
      <SnackAlert />

      <MediaDialog
        open={open}
        titleClassName={classes.title}
        title={hasParents ? "Wait!" : "Reason for Rejection"}
        handleClose={handleCloseClick}
        content={
          updateContent.isLoading ||
          removeContentFromCollection.isLoading ||
          deleteModuleItems.isLoading ? (
            <PanelLoadingIndicator />
          ) : (
            <Grid container direction="column" spacing={4}>
              <Grid item xs={12}>
                <Typography>
                  {hasParents
                    ? "Are you sure you want to reject? Rejecting this item will also remove it from the following content groups:"
                    : "In order to reject this item and hide it from your list of approved content, please provide a reason for rejection."}
                </Typography>
              </Grid>

              {hasParents ? (
                <List>
                  {map(
                    parent => (
                      <ListItem>
                        <ListItemText>{parent.name}</ListItemText>
                        <div className={classes.badge}>
                          <Typography className={classes.mediaType}>
                            {toUpper(parent.mediaType)}
                          </Typography>
                        </div>
                      </ListItem>
                    ),
                    parents,
                  )}
                </List>
              ) : (
                <Fragment>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={error}>
                      <InputLabel>Select a reason for rejection</InputLabel>
                      <Select
                        labelId="rejection-reason-label"
                        id="rejection-reason"
                        value={reason}
                        onChange={handleChange}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                        }}
                      >
                        {mapIndexed(
                          (reason, index) => (
                            <MenuItem key={`reason-${index}`} value={reason}>
                              {reason}
                            </MenuItem>
                          ),
                          sortBy(identity, rejectedReasons),
                        )}
                      </Select>
                      {error && (
                        <FormHelperText>Please provide a reason</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {equals(reason, "Other (provide reason)") && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        label="Other Reason"
                        value={otherReason}
                        onChange={event => setOtherReason(event.target.value)}
                        inputProps={{
                          maxLength: 4096,
                        }}
                        InputProps={{
                          classes: {
                            input: classes.otherReason,
                          },
                        }}
                      />
                    </Grid>
                  )}
                </Fragment>
              )}
            </Grid>
          )
        }
        actionsClassName={classes.actions}
        actions={
          <Grid container alignItems="center">
            <Button
              variant="contained"
              onClick={hasParents ? removeParents : submit}
              classes={{
                root: classes.button,
                contained: clsx({ [classes.rejected]: !hasParents }),
              }}
            >
              {hasParents ? "Remove File From Content Groups" : "Reject Item"}
            </Button>
            <Button onClick={handleCloseClick}>Cancel</Button>
          </Grid>
        }
      />
    </Fragment>
  );
};

export default RejectedDialog;
